import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, Sparkles, FileText, ArrowRight } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { DOCUMENT_TYPE_LABELS, type DocumentType } from '@praxara/shared/constants';

interface SearchHit {
  chunkId: string;
  documentId: string;
  text: string;
  score: number;
  chunkIndex: number;
  pageStart: number | null;
  section: string | null;
  document: { fileName: string; documentType: string | null };
}

export function SearchPage() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchHit[] | null>(null);

  const search = useMutation({
    mutationFn: async (query: string) =>
      (await api.get(`/api/search?q=${encodeURIComponent(query)}&limit=20`)).data,
    onSuccess: (data) => setResults(data.results as SearchHit[]),
    onError: (err) => toast.error('Search failed', extractApiError(err).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (q.trim().length < 2) return;
    search.mutate(q.trim());
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Semantic search</h1>
        <p className="text-sm text-muted-foreground">
          Find relevant passages across every document in your tenant. Powered by 1536-dim
          embeddings, not keyword matching — phrase it like you'd ask a colleague.
        </p>
      </div>

      <form onSubmit={onSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. adverse events related to the study drug, Phase III efficacy endpoints, CMC stability data at 40 degC..."
            className="pl-10"
            autoFocus
          />
        </div>
        <Button type="submit" disabled={search.isPending || q.trim().length < 2}>
          {search.isPending ? 'Searching...' : 'Search'}
        </Button>
      </form>

      {search.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : results === null ? (
        <EmptyState
          icon={Sparkles}
          title="Ask a question"
          description="The answer will be pulled from every document your tenant has uploaded. Results are ranked by semantic similarity, not keyword matches."
        />
      ) : results.length === 0 ? (
        <EmptyState icon={SearchIcon} title="No relevant passages" description="Try broader wording or confirm the document has been indexed." />
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            {results.length} passage{results.length !== 1 ? 's' : ''} found
          </div>
          {results.map((hit) => (
            <SearchResult key={hit.chunkId} hit={hit} />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchResult({ hit }: { hit: SearchHit }) {
  const scorePct = Math.round(hit.score * 100);
  const typeLabel = hit.document.documentType
    ? DOCUMENT_TYPE_LABELS[hit.document.documentType as DocumentType]
    : null;
  return (
    <Card className="transition-all hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Link
              to={`/documents/${hit.documentId}`}
              className="flex items-center gap-2 text-sm font-medium hover:text-primary"
            >
              <FileText className="h-4 w-4 text-muted-foreground" />
              {hit.document.fileName}
              <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
              {typeLabel && <span>{typeLabel}</span>}
              {hit.section && <span>? {hit.section}</span>}
              {hit.pageStart && <span>· page {hit.pageStart}</span>}
              <span>· chunk {hit.chunkIndex}</span>
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {scorePct}% match
          </Badge>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          {hit.text.length > 600 ? hit.text.slice(0, 600).trim() + '...' : hit.text}
        </p>
      </CardContent>
    </Card>
  );
}
